import { useEffect } from 'react';
import {
  $createRangeSelection,
  $getRoot,
  $setSelection,
  COMMAND_PRIORITY_HIGH,
  DROP_COMMAND,
  LexicalEditor,
} from 'lexical';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';

// dragover 시점엔 dataTransfer.files 가 비어 오므로(보안) items 의 kind/type 으로 판별
const hasExternalImageFile = (dataTransfer: DataTransfer | null): boolean => {
  if (!dataTransfer || !dataTransfer.types.includes('Files')) return false;
  if (dataTransfer.types.includes('application/x-lexical-drag')) return false;
  return Array.from(dataTransfer.items).some(
    (item) => item.kind === 'file' && item.type.startsWith('image/'),
  );
};

const caretRangeAtPoint = (event: DragEvent): Range | null => {
  if (document.caretRangeFromPoint) {
    return document.caretRangeFromPoint(event.clientX, event.clientY);
  }
  const doc = document as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
  };
  const position = doc.caretPositionFromPoint?.(event.clientX, event.clientY);
  if (!position) return null;
  const range = document.createRange();
  range.setStart(position.offsetNode, position.offset);
  range.collapse(true);
  return range;
};

const $selectDropPoint = (event: DragEvent, rootElement: HTMLElement | null) => {
  const range = caretRangeAtPoint(event);
  if (range && rootElement?.contains(range.startContainer)) {
    const selection = $createRangeSelection();
    selection.applyDOMRange(range);
    $setSelection(selection);
  } else {
    $getRoot().selectEnd();
  }
};

/**
 * 외부 이미지 파일 드랍을 에디터 영역(스크롤러 전체) 어디서든 받아 드랍 지점에 삽입한다.
 * selection 만 옮기고 업로드/검증/로딩노드는 DragDropPastePlugin(DRAG_DROP_PASTE)에 위임.
 */
export default function useDropImageAtPoint(editor: LexicalEditor) {
  useEffect(() => {
    const unregisterDrop = editor.registerCommand<DragEvent>(
      DROP_COMMAND,
      (event) => {
        if (!hasExternalImageFile(event.dataTransfer)) return false;
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (!files.length) return false;

        event.preventDefault();
        $selectDropPoint(event, editor.getRootElement());
        editor.dispatchCommand(DRAG_DROP_PASTE, files);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    let detachDomListeners: (() => void) | null = null;

    // root(본문) 밖 스크롤러 여백은 Lexical 이벤트가 안 닿으므로 DOM 레벨로 드랍 존 확장
    const unregisterRoot = editor.registerRootListener((rootElement) => {
      detachDomListeners?.();
      detachDomListeners = null;
      if (!rootElement) return;

      const dropZone =
        rootElement.closest<HTMLElement>('.editor-scroller') ?? rootElement;

      const onDragover = (event: DragEvent) => {
        if (!hasExternalImageFile(event.dataTransfer)) return;
        // 포커스(selection) 없는 상태에선 기본 핸들러가 드랍을 허용하지 않으므로 직접 허용
        event.preventDefault();
      };

      const onDrop = (event: DragEvent) => {
        // root 내부 드랍은 DROP_COMMAND 경로가 이미 처리(preventDefault)한 뒤 버블링됨 → 중복 방지
        if (event.defaultPrevented) return;
        if (!hasExternalImageFile(event.dataTransfer)) return;
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (!files.length) return;

        event.preventDefault();
        editor.update(() => {
          $selectDropPoint(event, rootElement);
        });
        editor.dispatchCommand(DRAG_DROP_PASTE, files);
      };

      dropZone.addEventListener('dragover', onDragover);
      dropZone.addEventListener('drop', onDrop);
      detachDomListeners = () => {
        dropZone.removeEventListener('dragover', onDragover);
        dropZone.removeEventListener('drop', onDrop);
      };
    });

    return () => {
      unregisterDrop();
      unregisterRoot();
      detachDomListeners?.();
    };
  }, [editor]);
}
